import * as React from 'react';
import ReactTable from 'react-table';
import { OptionsViewModel } from '@models/common.models';
import { SpinnerPage } from '../spinner/spinner-page.component';

export interface DoubleSelectTableComponentProperties {
  readonly storedData: OptionsViewModel[];
  readonly eligibleData: OptionsViewModel[];
  readonly storedLabel: string;
  readonly eligibleLabel: string;
  readonly saveCallback: (data: OptionsViewModel[]) => void;
  readonly deleteCallback?: (data: OptionsViewModel[]) => void;
  readonly showWeight?: boolean;
}

export interface DoubleSelectTableComponentState {
  readonly eligibleDataState: OptionsViewModel[] | null;
  readonly storedDataState: OptionsViewModel[] | null;
  readonly toBeDeleted: OptionsViewModel[];
  readonly toBeSaved: OptionsViewModel[];
}

/**
 * This component is for two side by side react tables where
 * the left is a list of available options
 * and the right is a list of saved / selected options.
 * Designed for similar functionality of
 * Brand / CVI Exceptions and Brand Certifications
 *
 * @export
 * @class DoubleSelectTableComponent
 * @extends {React.Component<DoubleSelectTableComponentProperties, DoubleSelectTableComponentState>}
 */
export class DoubleSelectTableComponent extends React.Component<
  DoubleSelectTableComponentProperties,
  DoubleSelectTableComponentState
> {
  constructor(props: DoubleSelectTableComponentProperties) {
    super(props);
    this.state = {
      eligibleDataState: null,
      storedDataState: null,
      toBeDeleted: [],
      toBeSaved: []
    };
  }

  componentWillReceiveProps(nextProps: DoubleSelectTableComponentProperties) {
    // Reorganize state on receipt of new data
    if (
      nextProps.eligibleData !== null &&
      nextProps.storedData !== null &&
      (nextProps.eligibleData !== this.props.eligibleData ||
        nextProps.storedData !== this.props.storedData ||
        !this.state.eligibleDataState ||
        !this.state.storedDataState)
    ) {
      this.initializeData(nextProps.eligibleData, nextProps.storedData);
    }
  }

  /**
   * Configure tables with left being the eligibleData
   * and right being the storedData
   *
   * @param {OptionsViewModel[]} eligibleData
   * @param {OptionsViewModel[]} storedData
   */
  initializeData = (eligibleData: OptionsViewModel[], storedData: OptionsViewModel[]) => {
    if (eligibleData && eligibleData.length) {
      // Filter stored data from eligible data
      const newEligibleData = eligibleData!
        .filter(
          eligibleObj =>
            eligibleObj.value !== '--' &&
            this.props.storedData!.filter(storedObj => storedObj.id === eligibleObj.id).length === 0
        )
        .sort(this.sort);
      this.setState({
        eligibleDataState: newEligibleData,
        storedDataState: storedData.sort(this.sort),
        toBeDeleted: [],
        toBeSaved: []
      });
    }
  };

  /**
   * Sort by Value
   *
   * @param {OptionsViewModel} a
   * @param {OptionsViewModel} b
   * @returns
   * @memberof DoubleSelectTableComponent
   */
  sort(a: OptionsViewModel, b: OptionsViewModel) {
    if (a.value > b.value) {
      return 1;
    } else if (a.value < b.value) {
      return -1;
    }
    return 0;
  }

  /**
   * Handle weight change
   * Specific to Brand Certifications
   *
   * @param {React.FormEvent<HTMLSelectElement>} event
   * @param {OptionsViewModel[]} storedData
   * @returns
   */
  weightChange = (event: React.FormEvent<HTMLSelectElement>, storedData: OptionsViewModel[]) => {
    var target = event.currentTarget;

    var item = {
      id: +target.id,
      value: target.name,
      order: target.value
    } as OptionsViewModel;
    this.setState({
      ...this.state,
      storedDataState: [item, ...storedData.filter(bc => bc.id !== item.id)].sort(this.sort)
    });
    return;
  };

  /**
   * Render method for left / eligibleData table
   * Takes in filteredData that does not include storedData
   *
   * @param {OptionsViewModel[]} filteredData
   * @returns
   * @memberof DoubleSelectTableComponent
   */
  renderEligibleTable(filteredData: OptionsViewModel[]) {
    return (
      <div className="col-md-6">
        <ReactTable
          onFilteredChange={filtered => {
            Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
              element.scrollTop = 0;
            });
          }}
          onPageChange={() => {
            Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
              element.scrollTop = 0;
            });
          }}
          className="-striped -highlight"
          key={filteredData.length}
          defaultPageSize={filteredData.length > 5 ? filteredData.length : 5}
          columns={[
            {
              accessor: 'original',
              Cell: (row: any) => (
                <div key={row.original.value}>
                  <p>
                    {row.original.value} <i className="fas fa-fw fr fa-chevron-right" />
                  </p>
                </div>
              ),
              Header: this.props.eligibleLabel,
              sortable: false,
              filterMethod: (filter: any, row: any) => {
                return (
                  String(row._original.value)
                    .toLowerCase()
                    .indexOf(filter.value.toLowerCase()) !== -1
                );
              }
            }
          ]}
          getTrProps={(state: any, rowInfo: any) => {
            return {
              onClick: () => this.toggleItem(rowInfo.original, true),
              className: 'pointer'
            };
          }}
          data={filteredData}
          showPagination={false}
          filterable={true}
        />
      </div>
    );
  }

  /**
   * Render method for right tabel,
   * contains stored data
   *
   * @param {OptionsViewModel[]} storedData
   * @returns
   * @memberof DoubleSelectTableComponent
   */
  renderStoredTable(storedData: OptionsViewModel[]) {
    return (
      this.state.storedDataState && (
        <div className="col-md-6">
          <ReactTable
            onFilteredChange={filtered => {
              Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
                element.scrollTop = 0;
              });
            }}
            onPageChange={() => {
              Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
                element.scrollTop = 0;
              });
            }}
            className="-striped -highlight"
            key={storedData.length}
            defaultPageSize={storedData.length > 5 ? storedData.length : 5}
            columns={[
              {
                accessor: 'original',
                Cell: (row: any) => (
                  <div key={row.original.value} className="row">
                    <div className="col-xs-8">
                      <p className="mt-10">
                        <i className="fas fa-fw fl fa-chevron-left mr-10 " /> {row.original.value}
                      </p>
                    </div>
                    {this.props.showWeight && (
                      <div className="col-md-3 mt-0" style={{ margin: 0 }}>
                        <label>Weight: </label>
                        <select
                          onChange={(event: React.FormEvent<HTMLSelectElement>) => {
                            this.weightChange(event, storedData);
                          }}
                          key={row.original.value}
                          defaultValue={row.original.order !== '' ? row.original.order : '1'}
                          onClick={(event: React.MouseEvent<HTMLSelectElement>) => {
                            event.stopPropagation();
                          }}
                          id={`${row.original.id}`}
                          name={`${row.original.value}`}
                        >
                          <option value={'1'}>1</option>
                          <option value={'2'}>2</option>
                          <option value={'3'}>3</option>
                          <option value={'4'}>4</option>
                          <option value={'5'}>5</option>
                        </select>
                      </div>
                    )}
                  </div>
                ),
                Header: this.props.storedLabel,
                sortable: false,
                filterMethod: (filter: any, row: any) => {
                  return (
                    String(row._original.value)
                      .toLowerCase()
                      .indexOf(filter.value.toLowerCase()) !== -1
                  );
                }
              }
            ]}
            getTrProps={(state: any, rowInfo: any) => {
              return {
                onClick: () => this.toggleItem(rowInfo.original, false),
                className: 'pointer'
              };
            }}
            data={storedData}
            showPagination={false}
            filterable={true}
          />
        </div>
      )
    );
  }

  /**
   * Shifts item between eligible and stored tables
   * Configures state for upcoming Delete / Save API calls
   *
   * @param {OptionsViewModel} item
   * @param {boolean} storing
   * @returns
   */
  toggleItem = (item: OptionsViewModel, storing: boolean) => {
    if (!this.state.eligibleDataState || !this.state.storedDataState) {
      return;
    }
    const filteredEligible = this.state.eligibleDataState.filter(obj => {
      return obj !== item;
    });

    const filteredStored = this.state.storedDataState.filter(obj => {
      return obj !== item;
    });

    this.setState({
      eligibleDataState: storing
        ? filteredEligible.sort(this.sort)
        : [item, ...this.state.eligibleDataState].sort(this.sort),
      storedDataState: storing ? [item, ...this.state.storedDataState].sort(this.sort) : filteredStored.sort(this.sort),
      toBeDeleted:
        !storing && this.props.storedData.some(e => e.id === item.id)
          ? [item, ...this.state.toBeDeleted]
          : this.state.toBeDeleted.filter(e => e.id !== item.id),
      toBeSaved:
        storing && !this.props.storedData.some(e => e.id === item.id)
          ? [item, ...this.state.toBeSaved]
          : this.state.toBeSaved.filter(e => e.id !== item.id)
    });
  };

  /**
   * Shift all items to left or right panel
   * Passing true shifts all data to right table
   *
   * @param {boolean} storing
   * @returns
   */
  toggleAll = (storing: boolean) => {
    if (!this.state.eligibleDataState || !this.state.storedDataState) {
      return;
    }

    const items = storing ? this.state.eligibleDataState : this.state.storedDataState;

    const filteredEligible = this.state.eligibleDataState.filter(item => items.indexOf(item) > -1);
    const filteredStored = this.state.storedDataState.filter(item => items.indexOf(item) > -1);

    const newEligibleDataState = storing ? [] : this.props.eligibleData.sort(this.sort);
    const newStoredDataState = storing ? this.props.eligibleData.sort(this.sort) : [];

    this.setState({
      eligibleDataState: newEligibleDataState,
      storedDataState: newStoredDataState,
      toBeDeleted: filteredStored,
      toBeSaved: filteredEligible
    });
  };

  /**
   * Resets both tables to intitial state and clears changes
   *
   */
  reset = () => {
    this.initializeData(this.props.eligibleData, this.props.storedData);
    this.setState({ toBeDeleted: [], toBeSaved: [] });
  };

  render() {
    const { eligibleDataState, storedDataState, toBeDeleted, toBeSaved } = this.state;
    if (!eligibleDataState || !storedDataState) {
      return <SpinnerPage />;
    } else {
      return (
        <div>
          <div className="row">
            {this.renderEligibleTable(eligibleDataState)}
            {this.renderStoredTable(storedDataState)}
          </div>
          <div className="row text-right">
            <div className="col-sm-6">
              <button className="btn btn-secondary pull-left" onClick={() => this.toggleAll(false)}>
                Deselect All
              </button>
              <button className="btn btn-primary pull-right" onClick={() => this.toggleAll(true)}>
                Select All
              </button>
            </div>
            <div className="col-sm-6">
              <button className="btn btn-secondary pull-left" type="reset" onClick={this.reset}>
                Clear
              </button>
              <button
                className="btn btn-primary pull-right"
                type="submit"
                onClick={() => {
                  if (this.props.deleteCallback) {
                    this.props.deleteCallback(toBeDeleted);
                    this.props.saveCallback(toBeSaved);
                  } else if (storedDataState) {
                    this.props.saveCallback(storedDataState);
                  }
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/common/components/double-select-table/double-select-table.component.tsx